/**
 * CIP slot plan: which ID fills Core's ID 1 (F254-258) and ID 2 slot, and what to write to person.extra.
 *
 * CHG-0135 (MacKenzie 10/29): the ranking only knew the IDs scanned since the person dialog opened. After a
 * reopen the driver's license was already on file but not in the ranking, so an SSN card scanned as the second
 * ID ranked first and replaced the primary ID type. IDs already on the person (an earlier scan, Core, or typed)
 * now take part in the ranking as incumbents: one that keeps its slot is never rewritten, and one that a better
 * ID outranks moves to the other slot with its values.
 *
 * CHG-0134 (MacKenzie 10/29): each capture's family is read from its stored extract, which the Apply step now
 * updates with the officer's corrections, so a correction survives the re-rank and lands in the right slot.
 */
import { assignCipSlots, type CapturedIdDoc, type CipIdPriorityRule } from './assignCipSlots';

/** Number / issuer / issue date / expiry for one ID. */
export interface CipFamily {
  idNumber: string;
  idIssuer: string;
  idIssueDate: string;
  idExpiryDate: string;
}

/** An ID scanned in this session (a Stage-4 tile with an F254 code). */
export interface SlotCapture {
  tileId: string;
  idCode: string;
  docTypeValue: string;
  family: CipFamily;
}

/** An ID already on the person: extra.idType + family (slot 1) or extra.idType2 + family (slot 2). */
export interface OnFileId {
  idType: string;
  family: CipFamily;
}

export type SlotSource = string; // a tileId, or ON_FILE_PRIMARY / ON_FILE_SECONDARY
export const ON_FILE_PRIMARY = '@on-file:primary';
export const ON_FILE_SECONDARY = '@on-file:secondary';

export interface CipSlotPlan {
  primary?: SlotSource;
  secondary?: SlotSource;
  extraPatch: Record<string, string>;
}

const FAMILY_KEYS: ReadonlyArray<keyof CipFamily> = ['idNumber', 'idIssuer', 'idIssueDate', 'idExpiryDate'];

export function planCipSlots(
  captures: ReadonlyArray<SlotCapture>,
  onFile: { primary?: OnFileId; secondary?: OnFileId },
  rules: ReadonlyArray<CipIdPriorityRule>,
): CipSlotPlan {
  const capturedCodes = new Set(captures.map((c) => c.idCode));
  const candidates: CapturedIdDoc[] = captures.map((c) => ({
    idTypeCode: c.idCode,
    docTypeKey: c.docTypeValue,
    sourceDocCode: c.tileId,
  }));
  // Incumbents rank like any ID; on a tie they were there first. One whose type was scanned this session is
  // that scan's own earlier write, so it isn't counted twice.
  const incumbents: Array<[SlotSource, OnFileId | undefined]> = [[ON_FILE_PRIMARY, onFile.primary], [ON_FILE_SECONDARY, onFile.secondary]];
  for (const [source, id] of incumbents) {
    if (id?.idType && !capturedCodes.has(id.idType)) {
      candidates.push({ idTypeCode: id.idType, sourceDocCode: source, capturedAt: '1970-01-01T00:00:00Z' });
    }
  }
  const { id1, id2 } = assignCipSlots(candidates, rules);

  const extraPatch: Record<string, string> = {};
  const write = (slot: 'primary' | 'secondary', source: SlotSource | undefined): void => {
    if (!source) return;
    const suffix = slot === 'primary' ? '' : '2';
    const here = slot === 'primary' ? ON_FILE_PRIMARY : ON_FILE_SECONDARY;
    if (source === here) return; // the incumbent keeps its slot: leave it as it is
    const current = slot === 'primary' ? onFile.primary : onFile.secondary;
    let idType: string;
    let family: CipFamily;
    if (source === ON_FILE_PRIMARY || source === ON_FILE_SECONDARY) {
      const moved = source === ON_FILE_PRIMARY ? onFile.primary! : onFile.secondary!;
      idType = moved.idType;
      family = moved.family;
    } else {
      const cap = captures.find((c) => c.tileId === source)!;
      idType = cap.idCode;
      family = cap.family;
    }
    // A different ID taking the slot replaces every field, so none of the old ID's values stay behind. The
    // same ID re-applied writes only the values it has, never blanking one the officer typed.
    const replacing = (current?.idType ?? '') !== idType;
    if (idType) extraPatch[`idType${suffix}`] = idType;
    for (const k of FAMILY_KEYS) {
      if (family[k] || replacing) extraPatch[`${k}${suffix}`] = family[k];
    }
  };
  write('primary', id1?.sourceDocCode);
  write('secondary', id2?.sourceDocCode);
  return {
    ...(id1?.sourceDocCode ? { primary: id1.sourceDocCode } : {}),
    ...(id2?.sourceDocCode ? { secondary: id2.sourceDocCode } : {}),
    extraPatch,
  };
}

/** The CIP keys a correction writes; for a ranked ID they go through the slot plan, not straight to slot 1. */
export const CIP_CORRECTION_KEYS: ReadonlySet<string> = new Set(['idType', ...FAMILY_KEYS]);

/**
 * Fold Apply-step corrections into a stored extract ({ key: { value, confidence } }), so the slot plan reads
 * the corrected family. Officer-typed values carry confidence -1 ("manual"), as in CaptureModal.
 */
export function extractWithCorrections(
  extract: Readonly<Record<string, unknown>>,
  corrections: Readonly<Record<string, string>>,
): Record<string, unknown> {
  const out: Record<string, unknown> = { ...extract };
  // The family keys, plus any key the extract itself carries (an SSN card's number is ssnNumber).
  for (const [k, v] of Object.entries(corrections)) {
    if ((FAMILY_KEYS as ReadonlyArray<string>).includes(k) || k in extract) out[k] = { value: v, confidence: -1 };
  }
  return out;
}
